import { format } from 'date-fns'

import type {
  ActivityLog,
  ActivityLogChange,
  ActivityLogFeedEntry,
  ActivityLogRecordType,
  ActivityLogTransformInput,
} from '../activityLog'
import type { History } from '../history'
import {
  ActivityLogChangeType,
  ActivityLogDocumentType,
  ActivityLogDocumentTypeLabels,
  ActivityLogPropertyChangeType,
} from '../constants/activityLog'
import {
  AdmissionGroupType,
  AdmissionGroupStatus,
} from '../constants/admissionGroup'
import { InventoryType, InventoryTypeName } from '../constants/inventory'
import { HistoryChangeType, HistoryType } from '../constants/history'
import {
  formatCancellationRefundLabel,
  getActivityFieldConfig,
  getActivityFieldLabel,
  isHiddenActivityKeyPath,
} from '../constants/activityFieldMap'
import { transformKeyPath } from './activityLogDisplay'
import { resolveActivityLogActor } from './activityLogActor'

const FACT_PRIORITY = [
  'name',
  'vip',
  'status',
  'tables',
  'eventName',
  'minimumSpend',
  'guestsExpected',
  'guestsCheckedIn',
  'serverName',
  'attributionName',
  'date',
  'lead',
  'assignedTo',
  'notes',
  'cancelledReasonType',
] as const

const MAX_FACTS = 5
const MAX_FACT_VALUE_LENGTH = 120
const HTML_PATTERN = /<[^>]+>/
const MAX_TIMELINE_VALUE_LENGTH = 160
const TIMELINE_MS_TIMESTAMP_HEURISTIC = 1_000_000_000_000

type NormalizedEventType = 'created' | 'updated' | 'deleted'

function normalizeEventType(
  type: ActivityLogChangeType | HistoryType
): NormalizedEventType | null {
  if (
    type === ActivityLogChangeType.CREATED_DOCUMENT ||
    type === HistoryType.CREATED
  ) {
    return 'created'
  }
  if (
    type === ActivityLogChangeType.UPDATED_DOCUMENT ||
    type === HistoryType.UPDATED
  ) {
    return 'updated'
  }
  if (
    type === ActivityLogChangeType.DELETED_DOCUMENT ||
    type === HistoryType.DELETED
  ) {
    return 'deleted'
  }
  return null
}

function mapRecordType(
  admissionGroupType?: AdmissionGroupType
): ActivityLogRecordType {
  switch (admissionGroupType) {
    case AdmissionGroupType.TABLE:
      return 'reservation'
    case AdmissionGroupType.GUEST_LIST:
      return 'guestlist'
    case AdmissionGroupType.TICKET:
      return 'ticket'
    case AdmissionGroupType.TALLY:
      return 'tally'
    default:
      return 'record'
  }
}

function isEmptyValue(value: unknown): boolean {
  if (value == null || value === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

function isSafeFactValue(value: string): boolean {
  if (!value.trim()) return false
  if (value.length > MAX_FACT_VALUE_LENGTH) return false
  if (HTML_PATTERN.test(value)) return false
  return true
}

function formatFactValue(keyPath: string, value: unknown): string | null {
  const config = getActivityFieldConfig(keyPath)
  if (config.hidden) return null

  // Reject oversized raw strings before formatters truncate them into facts.
  if (typeof value === 'string' && value.length > MAX_FACT_VALUE_LENGTH) {
    return null
  }

  const formatted = config.formatValue
    ? config.formatValue(value)
    : value != null
      ? String(value)
      : null

  if (!formatted || !isSafeFactValue(formatted)) return null
  return formatted
}

function buildFacts(
  changes: ActivityLogChange[]
): { label: string; value: string }[] {
  const candidates: { label: string; value: string; priority: number }[] = []

  for (const change of changes) {
    if (change.changeType !== ActivityLogPropertyChangeType.ADDED_PROPERTY) {
      continue
    }

    const config = getActivityFieldConfig(change.keyPath)
    if (config.hidden || isEmptyValue(change.newValue)) continue

    const value = formatFactValue(change.keyPath, change.newValue)
    if (!value) continue

    const label = config.label || getActivityFieldLabel(change.keyPath)
    const priorityIndex = FACT_PRIORITY.indexOf(
      change.keyPath as (typeof FACT_PRIORITY)[number]
    )
    const priority =
      config.priority ??
      (priorityIndex >= 0 ? priorityIndex + 1 : FACT_PRIORITY.length + 1)

    candidates.push({ label, value, priority })
  }

  return candidates
    .sort((a, b) => a.priority - b.priority)
    .slice(0, MAX_FACTS)
    .map(({ label, value }) => ({ label, value }))
}

function buildUpdatePhrase(change: ActivityLogChange): string | null {
  const config = getActivityFieldConfig(change.keyPath)
  if (config.hidden) return null

  if (config.updatePhrase) {
    return config.updatePhrase({
      oldValue: change.oldValue,
      newValue: change.newValue,
    })
  }

  const label = config.label || getActivityFieldLabel(change.keyPath)

  if (change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY) {
    return `removed ${label.toLowerCase()}`
  }

  if (change.changeType === ActivityLogPropertyChangeType.ADDED_PROPERTY) {
    const value = formatFactValue(change.keyPath, change.newValue)
    return value ? `set ${label.toLowerCase()} to ${value}` : null
  }

  const newValue = formatFactValue(change.keyPath, change.newValue)
  return newValue ? `changed ${label.toLowerCase()} to ${newValue}` : null
}

function joinPhrases(phrases: string[]): string {
  if (phrases.length === 0) return ''
  if (phrases.length === 1) return phrases[0]
  if (phrases.length === 2) return `${phrases[0]} and ${phrases[1]}`
  return `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`
}

const MAX_TABLE_SUMMARY_PARTS = 4

function joinListParts(parts: string[]): string {
  if (parts.length === 0) return ''
  if (parts.length <= MAX_TABLE_SUMMARY_PARTS) {
    return parts.join(', ')
  }
  const shown = parts.slice(0, MAX_TABLE_SUMMARY_PARTS)
  const remaining = parts.length - MAX_TABLE_SUMMARY_PARTS
  return `${shown.join(', ')}, +${remaining} more`
}

function isInventoryNameField(
  keyPath: string,
  documentType?: ActivityLogDocumentType
): boolean {
  return (
    documentType === ActivityLogDocumentType.INVENTORY && keyPath === 'name'
  )
}

function getChangeValue(
  changes: ActivityLogChange[],
  keyPath: string,
  changeTypes: ActivityLogPropertyChangeType[]
): unknown {
  const change = changes.find(
    (entry) =>
      entry.keyPath === keyPath && changeTypes.includes(entry.changeType)
  )

  if (!change) return undefined

  if (change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY) {
    return change.oldValue
  }

  return change.newValue
}

function getAdmissionGroupTypeTableSummaryLabel(
  type: AdmissionGroupType | string
): string {
  switch (type) {
    case AdmissionGroupType.TABLE:
      return 'Table'
    case AdmissionGroupType.GUEST_LIST:
      return 'Guest list'
    case AdmissionGroupType.TICKET:
      return 'Ticket'
    case AdmissionGroupType.TALLY:
      return 'Tally'
    default:
      return 'Reservation'
  }
}

const ADMISSION_GROUP_DISPLAY_TEXT_LEAD =
  /^(Reservation|Guest list|Tally|Ticket) for (.+?),(?: created| updated| deleted)/i

function parseAdmissionGroupLeadFromDisplayText(
  displayText?: string | null
): { typeLabel: string; name: string } | null {
  if (!displayText?.trim()) return null

  const lead = displayText.split(/\n\n+/)[0]?.trim() ?? ''
  const match = lead.match(ADMISSION_GROUP_DISPLAY_TEXT_LEAD)
  if (!match?.[1] || !match[2]) return null

  const displayType = match[1].trim().toLowerCase()
  const typeLabel =
    displayType === 'reservation'
      ? 'Table'
      : displayType.charAt(0).toUpperCase() + displayType.slice(1)

  return {
    typeLabel,
    name: match[2].trim(),
  }
}

function getFieldValueFromChanges(
  changes: ActivityLogChange[],
  keyPath: string
): unknown {
  const change = changes.find((entry) => entry.keyPath === keyPath)
  if (!change) return undefined

  if (change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY) {
    return change.oldValue
  }

  return change.newValue ?? change.oldValue
}

function formatCancelledReasonLabel(value: unknown): string | null {
  if (value == null || value === '') return null

  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function buildAdmissionGroupCancellationSummary(args: {
  changes: ActivityLogChange[]
  displayText?: string | null
}): string | null {
  let typeValue = getFieldValueFromChanges(args.changes, 'type')
  let typeLabel =
    typeof typeValue === 'string'
      ? getAdmissionGroupTypeTableSummaryLabel(typeValue)
      : null

  let nameValue = getFieldValueFromChanges(args.changes, 'name')
  let name =
    nameValue != null && String(nameValue).trim()
      ? String(nameValue).trim()
      : null

  if (!typeLabel || !name) {
    const parsed = parseAdmissionGroupLeadFromDisplayText(args.displayText)
    typeLabel = typeLabel ?? parsed?.typeLabel ?? null
    name = name ?? parsed?.name ?? null
  }

  const reason = formatCancelledReasonLabel(
    getFieldValueFromChanges(args.changes, 'cancelledReasonType')
  )
  const refundLabel = formatCancellationRefundLabel(
    getFieldValueFromChanges(args.changes, 'cancellationRefund')
  )

  let recordSummary = ''
  if (typeLabel && name) {
    recordSummary = `${typeLabel} for ${name}`
  } else if (name) {
    recordSummary = name
  } else if (typeLabel) {
    recordSummary = typeLabel
  }

  const summaryParts: string[] = []
  if (reason) {
    summaryParts.push(
      recordSummary
        ? `${recordSummary}. Reason: ${reason}`
        : `Reason: ${reason}`
    )
  } else if (recordSummary) {
    summaryParts.push(recordSummary)
  }

  if (refundLabel) summaryParts.push(refundLabel)

  if (summaryParts.length === 0) return null
  return summaryParts.join('. ')
}

function buildAdmissionGroupRecordSummary(args: {
  changes: ActivityLogChange[]
  displayText?: string | null
  changeTypes: ActivityLogPropertyChangeType[]
}): string | null {
  const typeValue = getChangeValue(args.changes, 'type', args.changeTypes)
  let typeLabel =
    typeof typeValue === 'string'
      ? getAdmissionGroupTypeTableSummaryLabel(typeValue)
      : null

  const nameValue = getChangeValue(args.changes, 'name', args.changeTypes)
  let name =
    nameValue != null && String(nameValue).trim()
      ? String(nameValue).trim()
      : null

  if (!typeLabel || !name) {
    const parsed = parseAdmissionGroupLeadFromDisplayText(args.displayText)
    typeLabel = typeLabel ?? parsed?.typeLabel ?? null
    name = name ?? parsed?.name ?? null
  }

  if (typeLabel && name) return `${typeLabel} for ${name}`
  if (name) return name
  if (typeLabel) return typeLabel
  return null
}

function buildInventoryFieldSnapshot(
  changes: ActivityLogChange[]
): Record<string, unknown> {
  const snapshot: Record<string, unknown> = {}

  for (const change of changes) {
    if (!['type', 'name', 'location'].includes(change.keyPath)) continue

    let value: unknown
    if (change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY) {
      value = change.oldValue
    } else if (
      change.changeType === ActivityLogPropertyChangeType.ADDED_PROPERTY
    ) {
      value = change.newValue
    } else if (
      change.changeType === ActivityLogPropertyChangeType.UPDATED_PROPERTY
    ) {
      value = change.newValue ?? change.oldValue
    } else {
      continue
    }

    if (value !== undefined && value !== null && value !== '') {
      snapshot[change.keyPath] = value
    }
  }

  return snapshot
}

function resolveInventoryNameOnly(
  snapshot: Record<string, unknown>
): string | null {
  const name = snapshot.name
  const location = snapshot.location

  if (typeof name === 'string' && name.trim()) return name.trim()
  if (typeof location === 'string' && location.trim()) return location.trim()
  return null
}

function getInventoryTypeLabelFromSnapshot(
  snapshot: Record<string, unknown>
): string | null {
  const type = snapshot.type
  if (typeof type !== 'string') return null

  return InventoryTypeName[type as InventoryType] ?? null
}

function formatInventorySummaryLabel(
  snapshot: Record<string, unknown>
): string | null {
  const typeLabel = getInventoryTypeLabelFromSnapshot(snapshot)
  const name = resolveInventoryNameOnly(snapshot)

  if (typeLabel && name) return `${typeLabel} ${name}`
  if (name) return name
  if (typeLabel) return typeLabel
  return null
}

function parseInventoryTypeFromDisplayText(
  displayText?: string | null
): string | null {
  if (!displayText?.trim()) return null

  const match = displayText.match(/Removed Type:\s*"?([A-Z_]+)"?/i)
  if (!match?.[1]) return null

  return getInventoryTypeLabelFromSnapshot({ type: match[1] })
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function buildInventoryCreatedSummary(
  changes: ActivityLogChange[]
): string | null {
  return formatInventorySummaryFromChanges(changes)
}

export function formatInventorySummaryFromChanges(
  changes: ActivityLogChange[]
): string | null {
  return formatInventorySummaryLabel(buildInventoryFieldSnapshot(changes))
}

function buildCompactCreatedSummary(args: {
  changes: ActivityLogChange[]
  documentType?: ActivityLogDocumentType
  displayText?: string | null
}): string | null {
  switch (args.documentType) {
    case ActivityLogDocumentType.ADMISSION_GROUP:
      return buildAdmissionGroupRecordSummary({
        changes: args.changes,
        displayText: args.displayText,
        changeTypes: [ActivityLogPropertyChangeType.ADDED_PROPERTY],
      })
    case ActivityLogDocumentType.INVENTORY:
      return buildInventoryCreatedSummary(args.changes)
    case ActivityLogDocumentType.CUSTOMER: {
      const name = getChangeValue(args.changes, 'name', [
        ActivityLogPropertyChangeType.ADDED_PROPERTY,
      ])
      return name != null && String(name).trim() ? String(name).trim() : null
    }
    default:
      return null
  }
}

function buildCompactDeletedSummary(args: {
  changes: ActivityLogChange[]
  documentType?: ActivityLogDocumentType
  displayText?: string | null
}): string | null {
  if (args.documentType === ActivityLogDocumentType.INVENTORY) {
    return getDeletedInventoryName(args.changes, args.displayText)
  }

  if (args.documentType === ActivityLogDocumentType.ADMISSION_GROUP) {
    return buildAdmissionGroupRecordSummary({
      changes: args.changes,
      displayText: args.displayText,
      changeTypes: [ActivityLogPropertyChangeType.DELETED_PROPERTY],
    })
  }

  if (args.documentType === ActivityLogDocumentType.CUSTOMER) {
    const name = getChangeValue(args.changes, 'name', [
      ActivityLogPropertyChangeType.DELETED_PROPERTY,
    ])
    return name != null && String(name).trim() ? String(name).trim() : null
  }

  return null
}

function toSummaryFieldLabel(label: string): string {
  return label.toLowerCase().replace(/\bvip\b/g, 'VIP')
}

function getDocumentTypeSummaryLabel(
  documentType?: ActivityLogDocumentType
): string {
  if (!documentType) return 'record'
  if (documentType === ActivityLogDocumentType.ADMISSION_GROUP) {
    return 'admission group'
  }
  return ActivityLogDocumentTypeLabels[documentType].toLowerCase()
}

function buildCreatedSummaryParts(
  changes: ActivityLogChange[],
  documentType?: ActivityLogDocumentType
): string[] {
  const candidates: { part: string; priority: number }[] = []

  for (const change of changes) {
    if (change.changeType !== ActivityLogPropertyChangeType.ADDED_PROPERTY) {
      continue
    }
    if (isHiddenActivityKeyPath(change.keyPath)) continue
    if (isEmptyValue(change.newValue)) continue

    const config = getActivityFieldConfig(change.keyPath)
    const value = formatFactValue(change.keyPath, change.newValue)
    const label = isInventoryNameField(change.keyPath, documentType)
      ? null
      : toSummaryFieldLabel(config.label || transformKeyPath(change.keyPath))
    const part = label ? (value ? `${label} ${value}` : label) : value
    if (!part) continue
    const priorityIndex = FACT_PRIORITY.indexOf(
      change.keyPath as (typeof FACT_PRIORITY)[number]
    )
    const priority =
      config.priority ??
      (priorityIndex >= 0 ? priorityIndex + 1 : FACT_PRIORITY.length + 1)

    candidates.push({ part, priority })
  }

  return candidates
    .sort((a, b) => a.priority - b.priority)
    .map(({ part }) => part)
}

function stripRedundantEventVerb(phrase: string): string {
  return phrase.replace(/^(created|updated|deleted|cancelled)\s+/i, '')
}

function isCancellationUpdate(changes: ActivityLogChange[]): boolean {
  return changes.some(
    (change) =>
      change.keyPath === 'status' &&
      change.newValue === AdmissionGroupStatus.CANCELLED
  )
}

function isRefundUpdate(changes: ActivityLogChange[]): boolean {
  return changes.some((change) => change.keyPath === 'paymentRefund')
}

function isCancellationStatusChange(change: ActivityLogChange): boolean {
  return (
    change.keyPath === 'status' &&
    change.newValue === AdmissionGroupStatus.CANCELLED
  )
}

function getDeletedInventoryNameFromDisplayText(
  displayText?: string | null
): string | null {
  if (!displayText?.trim()) return null

  const lead = displayText.split(/\n\n+/)[0]?.trim() ?? ''
  const typedLeadMatch = lead.match(
    /^(Table|Ticket|Package|Cover) (.+?), deleted(?: by .+)?$/i
  )
  if (typedLeadMatch?.[1] && typedLeadMatch?.[2]) {
    return `${typedLeadMatch[1]} ${typedLeadMatch[2].trim()}`
  }

  const leadMatch = lead.match(/^Inventory (.+?), deleted(?: by .+)?$/i)
  if (leadMatch?.[1]) return leadMatch[1].trim()

  const removedMatch = displayText.match(
    /Removed (?:Location|Name):\s*"?([^"\n]+)"?/i
  )
  return removedMatch?.[1]?.trim() ?? null
}

export function resolveInventoryDisplayNameFromValue(
  oldValue: unknown
): string | null {
  if (!oldValue || typeof oldValue !== 'object') return null

  const inventory = oldValue as {
    name?: string | null
    location?: string | null
    type?: InventoryType
  }

  const explicitName = inventory.name?.trim() || inventory.location?.trim()
  if (explicitName) return explicitName

  if (inventory.type && InventoryTypeName[inventory.type]) {
    return InventoryTypeName[inventory.type]
  }

  return null
}

function getDeletedInventoryName(
  changes: ActivityLogChange[],
  displayText?: string | null
): string | null {
  const snapshot = buildInventoryFieldSnapshot(changes)
  const summaryFromChanges = formatInventorySummaryLabel(snapshot)

  if (summaryFromChanges && getInventoryTypeLabelFromSnapshot(snapshot)) {
    return summaryFromChanges
  }

  const displaySummary = getDeletedInventoryNameFromDisplayText(displayText)
  if (displaySummary) {
    if (summaryFromChanges && !getInventoryTypeLabelFromSnapshot(snapshot)) {
      const typeLabel = parseInventoryTypeFromDisplayText(displayText)
      const name = resolveInventoryNameOnly(snapshot)
      if (typeLabel && name) return `${typeLabel} ${name}`
    }

    const typeLabel =
      getInventoryTypeLabelFromSnapshot(snapshot) ??
      parseInventoryTypeFromDisplayText(displayText)
    if (
      typeLabel &&
      !new RegExp(`^${escapeRegExp(typeLabel)}\\b`, 'i').test(displaySummary)
    ) {
      return `${typeLabel} ${displaySummary}`
    }

    return displaySummary
  }

  return summaryFromChanges
}

function buildTableUpdatePhrase(
  change: ActivityLogChange,
  documentType?: ActivityLogDocumentType
): string | null {
  if (isHiddenActivityKeyPath(change.keyPath)) return null

  if (isInventoryNameField(change.keyPath, documentType)) {
    const value = formatFactValue(
      change.keyPath,
      change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY
        ? change.oldValue
        : change.newValue
    )
    if (change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY) {
      return value ?? 'removed name'
    }
    if (change.changeType === ActivityLogPropertyChangeType.ADDED_PROPERTY) {
      return value ? `set name to ${value}` : 'set name'
    }
    return value ? `name to ${value}` : 'name'
  }

  const phrase = buildUpdatePhrase(change)
  if (phrase) return stripRedundantEventVerb(phrase)

  const label = getActivityFieldLabel(change.keyPath).toLowerCase()

  if (change.changeType === ActivityLogPropertyChangeType.DELETED_PROPERTY) {
    return `removed ${label}`
  }
  if (change.changeType === ActivityLogPropertyChangeType.ADDED_PROPERTY) {
    return `set ${label}`
  }
  return `changed ${label}`
}

export function formatActivityLogTableSummary(args: {
  type: ActivityLogChangeType | HistoryType
  changes: ActivityLogChange[]
  documentType?: ActivityLogDocumentType
  displayText?: string | null
}): string {
  const eventType = normalizeEventType(args.type)
  if (!eventType) return ''

  if (eventType === 'created') {
    const compactSummary = buildCompactCreatedSummary(args)
    if (compactSummary) {
      return `Created: ${compactSummary}`
    }

    const parts = buildCreatedSummaryParts(args.changes, args.documentType)
    if (parts.length === 0) {
      return `Created: ${getDocumentTypeSummaryLabel(args.documentType)}`
    }
    return `Created: ${joinListParts(parts)}`
  }

  if (eventType === 'deleted') {
    const compactSummary = buildCompactDeletedSummary(args)
    if (compactSummary) {
      return `Deleted: ${compactSummary}`
    }

    const phrases = args.changes
      .map((change) => buildTableUpdatePhrase(change, args.documentType))
      .filter((phrase): phrase is string => Boolean(phrase))

    if (phrases.length > 0) {
      return `Deleted: ${joinPhrases(phrases)}`
    }

    return `Deleted: ${getDocumentTypeSummaryLabel(args.documentType)}`
  }

  const isRefund = isRefundUpdate(args.changes)
  const isCancellation = isCancellationUpdate(args.changes)

  if (
    isCancellation &&
    args.documentType === ActivityLogDocumentType.ADMISSION_GROUP
  ) {
    const cancellationSummary = buildAdmissionGroupCancellationSummary({
      changes: args.changes,
      displayText: args.displayText,
    })
    if (cancellationSummary) {
      return `Cancelled: ${cancellationSummary}`
    }
  }

  const prefix = isRefund
    ? 'Refunded'
    : isCancellation
      ? 'Cancelled'
      : 'Updated'

  const phrases = args.changes
    .filter(
      (change) =>
        (!isCancellation || !isCancellationStatusChange(change)) &&
        (!isRefund || change.keyPath === 'paymentRefund')
    )
    .map((change) => buildTableUpdatePhrase(change, args.documentType))
    .filter((phrase): phrase is string => Boolean(phrase))

  if (phrases.length === 0) {
    return prefix
  }

  return `${prefix}: ${joinPhrases(phrases)}`
}

function toIsoTimestamp(created: Date | string | number): string {
  if (created instanceof Date) return created.toISOString()
  if (typeof created === 'number') return new Date(created).toISOString()
  const parsed = new Date(created)
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString()
  return format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
}

export function transformActivityLog(
  raw: ActivityLogTransformInput
): ActivityLogFeedEntry | null {
  const eventType = normalizeEventType(raw.type)
  if (!eventType) return null

  const recordType = mapRecordType(raw.admissionGroupType)
  const actorName = raw.actor.name?.trim() || 'Someone'

  if (eventType === 'created') {
    const facts = buildFacts(raw.changes)
    return {
      id: raw.id,
      actor: raw.actor,
      timestamp: toIsoTimestamp(raw.created),
      recordType,
      headline: `${actorName} created this ${recordType}`,
      facts,
      rawChanges: raw.changes,
      displayText: raw.displayText,
    }
  }

  if (eventType === 'deleted') {
    return {
      id: raw.id,
      actor: raw.actor,
      timestamp: toIsoTimestamp(raw.created),
      recordType,
      headline: `${actorName} deleted this ${recordType}`,
      facts: [],
      rawChanges: raw.changes,
      displayText: raw.displayText,
    }
  }

  const phrases = raw.changes
    .map((change) => buildUpdatePhrase(change))
    .filter((phrase): phrase is string => Boolean(phrase))

  if (phrases.length === 0) return null

  return {
    id: raw.id,
    actor: raw.actor,
    timestamp: toIsoTimestamp(raw.created),
    recordType,
    headline: joinPhrases(phrases),
    facts: [],
    rawChanges: raw.changes,
    displayText: raw.displayText,
  }
}

export type HistoryTimelineChange = {
  keyPath: string
  label: string
  changeType: ActivityLogPropertyChangeType
  beforeValue?: string
  afterValue?: string
}

export type HistoryTimelineItem = {
  id: string
  created: Date | string | number
  actor: {
    name: string
    email?: string
    avatarUrl?: string
  }
  title: string
  changes: HistoryTimelineChange[]
}

function parseSortTimestamp(created: Date | string | number): number {
  if (created instanceof Date) return created.getTime()
  if (typeof created === 'number') return created

  const parsed = new Date(created).getTime()
  return Number.isNaN(parsed) ? 0 : parsed
}

function compareNewestFirstByCreatedAndId(
  a: Pick<HistoryTimelineItem, 'created' | 'id'>,
  b: Pick<HistoryTimelineItem, 'created' | 'id'>
): number {
  const aTs = parseSortTimestamp(a.created)
  const bTs = parseSortTimestamp(b.created)

  if (aTs !== bTs) return bTs - aTs

  return b.id.localeCompare(a.id)
}

function asAdmissionGroupType(value: unknown): AdmissionGroupType | undefined {
  if (!value || typeof value !== 'string') return undefined

  return Object.values(AdmissionGroupType).includes(value as AdmissionGroupType)
    ? (value as AdmissionGroupType)
    : undefined
}

function inferAdmissionGroupTypeFromChanges(
  changes: ActivityLogChange[]
): AdmissionGroupType | undefined {
  const typeChange = changes.find((change) => change.keyPath === 'type')
  if (!typeChange) return undefined

  return (
    asAdmissionGroupType(typeChange.newValue) ??
    asAdmissionGroupType(typeChange.oldValue)
  )
}

function getTimelineValueText(keyPath: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Empty'

  const config = getActivityFieldConfig(keyPath)
  const formatted = config.formatValue?.(value)
  if (formatted) return formatted

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') {
    if (value > TIMELINE_MS_TIMESTAMP_HEURISTIC) {
      const date = new Date(value)
      if (!Number.isNaN(date.getTime())) {
        return format(date, "MMM d, yyyy 'at' h:mm a")
      }
    }
    return String(value)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return 'Empty'
    if (trimmed.length > MAX_TIMELINE_VALUE_LENGTH) {
      return `${trimmed.slice(0, MAX_TIMELINE_VALUE_LENGTH - 1)}…`
    }
    return trimmed
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return 'No items'
    return `${value.length} item${value.length === 1 ? '' : 's'}`
  }

  if (typeof value === 'object') {
    const fieldCount = Object.keys(value as Record<string, unknown>).length
    return `${fieldCount} field${fieldCount === 1 ? '' : 's'}`
  }

  return String(value)
}

function mapTimelineChange(
  change: History['changes'][number] | ActivityLogChange
): ActivityLogChange {
  if ('changeType' in change) {
    return change as ActivityLogChange
  }

  return {
    changeType:
      change.type === HistoryChangeType.ADDED_PROPERTY
        ? ActivityLogPropertyChangeType.ADDED_PROPERTY
        : change.type === HistoryChangeType.DELETED_PROPERTY
          ? ActivityLogPropertyChangeType.DELETED_PROPERTY
          : ActivityLogPropertyChangeType.UPDATED_PROPERTY,
    keyPath: change.keyPath,
    oldValue: change.oldValue,
    newValue: change.newValue,
  }
}

function isCancelledHistoryEvent(changes: ActivityLogChange[]): boolean {
  return changes.some(
    (change) =>
      change.keyPath === 'status' &&
      String(change.newValue || '').toUpperCase() ===
        AdmissionGroupStatus.CANCELLED
  )
}

function capitalizeSentence(value: string): string {
  if (!value) return value
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

function getConfirmationNotificationTimelineTitle(
  changes: ActivityLogChange[]
): string | null {
  const notificationChange = changes.find(
    (change) => change.keyPath === 'confirmationNotification'
  )
  if (!notificationChange) return null

  const notificationConfig = getActivityFieldConfig('confirmationNotification')
  const formatNotification = notificationConfig.formatValue
  if (!formatNotification) return null

  const formatted = formatNotification(
    notificationChange.newValue ?? notificationChange.oldValue
  )
  if (!formatted) return null

  return capitalizeSentence(formatted)
}

function getTimelineTitle(args: {
  type: ActivityLogChangeType | HistoryType
  admissionGroupType?: AdmissionGroupType
  changes: ActivityLogChange[]
}): string {
  const recordType = mapRecordType(args.admissionGroupType)
  const recordLabel = recordType === 'record' ? 'reservation' : recordType

  if (args.type === HistoryType.CREATED) {
    return `Created this ${recordLabel}`
  }

  if (args.type === HistoryType.DELETED) {
    return `Deleted this ${recordLabel}`
  }

  if (isCancelledHistoryEvent(args.changes)) {
    return `Cancelled this ${recordLabel}`
  }

  const confirmationNotificationTitle =
    getConfirmationNotificationTimelineTitle(args.changes)
  if (confirmationNotificationTitle) {
    return confirmationNotificationTitle
  }

  return `Updated this ${recordLabel}`
}

function isActivityLogRow(row: History | ActivityLog): row is ActivityLog {
  return (
    'actorType' in row ||
    'actorName' in row ||
    'actorEmail' in row ||
    'managementUserName' in row ||
    'managementUserEmail' in row ||
    'managementUserProfileImage' in row ||
    'admissionGroupContext' in row
  )
}

function getChangePriority(keyPath: string): number {
  const config = getActivityFieldConfig(keyPath)
  if (typeof config.priority === 'number') return config.priority
  return FACT_PRIORITY.length + 100
}

export function transformHistoryTimeline(
  rows: Array<History | ActivityLog>,
  options: {
    admissionGroupType?: AdmissionGroupType
  } = {}
): HistoryTimelineItem[] {
  const mappedRows = rows
    .map((row) => {
      const activityChanges = (row.changes ?? []).map(mapTimelineChange)

      const admissionGroupType =
        options.admissionGroupType ??
        inferAdmissionGroupTypeFromChanges(activityChanges)

      const visibleChanges = activityChanges
        .map((change, index) => ({ change, index }))
        .filter(({ change }) => !getActivityFieldConfig(change.keyPath).hidden)
        .sort((a, b) => {
          const priorityDiff =
            getChangePriority(a.change.keyPath) -
            getChangePriority(b.change.keyPath)
          if (priorityDiff !== 0) return priorityDiff
          return a.index - b.index
        })
        .map(({ change }) => ({
          keyPath: change.keyPath,
          label: getActivityFieldLabel(change.keyPath),
          changeType: change.changeType,
          beforeValue:
            change.changeType !== ActivityLogPropertyChangeType.ADDED_PROPERTY
              ? getTimelineValueText(change.keyPath, change.oldValue)
              : undefined,
          afterValue:
            change.changeType !== ActivityLogPropertyChangeType.DELETED_PROPERTY
              ? getTimelineValueText(change.keyPath, change.newValue)
              : undefined,
        }))

      const isActivityLog = isActivityLogRow(row)
      const activityActor = isActivityLog
        ? resolveActivityLogActor(row)
        : undefined

      return {
        id: row.id,
        created: row.created,
        actor: {
          name: isActivityLog
            ? (activityActor?.actorName ?? 'Unknown')
            : (row.changedByName ?? 'Unknown'),
          email: isActivityLog
            ? (activityActor?.actorEmail ?? undefined)
            : (row.changedByEmail ?? undefined),
          avatarUrl: isActivityLog
            ? (row.managementUserProfileImage ?? undefined)
            : (row.changedByProfileImage ?? undefined),
        },
        title: getTimelineTitle({
          type: row.type,
          admissionGroupType,
          changes: activityChanges,
        }),
        changes: visibleChanges,
      }
    })
    .sort(compareNewestFirstByCreatedAndId)

  return mappedRows
}
